import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from '@socialchorus/shared-ui-components';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import { useChatbotStreaming } from '../../common/streaming/use-chatbot-streaming';
import { Suggestion } from '../../models/chatbot/types';

import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import './chatbot.scss';

interface ChatbotProps {
  threadId?: string;
  isThreadPersisted?: boolean;
  onThreadChange?: (threadId: string, persisted: boolean) => void;
  className?: string;
  suggestions?: Suggestion[];
}

const Chatbot: React.FC<ChatbotProps> = ({
  threadId,
  isThreadPersisted = false,
  onThreadChange,
  className = '',
  suggestions = [],
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showTimeoutError, setShowTimeoutError] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);

  const { t } = useTranslation();
  const programId = usePatronSelector(programSelectors.getProgramId);
  const streamMessage = useChatbotStreaming(programId);

  const handleSendMessage = async (messageToSend?: string) => {
    const message = messageToSend || messageInput.trim();

    if (!message || isSending || !programId) return;

    // Reset timeout error state for fresh attempt
    setShowTimeoutError(false);

    // Store message for potential restoration on timeout
    pendingMessageRef.current = message;
    setMessageInput('');

    try {
      // Start streaming - IDs will come via stream_start event
      streamMessage(
        { threadId, message: message },
        (event) => {
          switch (event.type) {
            case 'stream_start':
              // If this is a new conversation, update the threadId immediately but mark as not persisted
              if (!threadId && event.data?.threadId && onThreadChange) {
                onThreadChange(event.data.threadId, false);
              }
              // Set typing indicator after user message is added to cache
              setIsSending(true);
              break;

            case 'stream_end': {
              // Clear pending message on success
              pendingMessageRef.current = null;
              // If this was a new conversation (no threadId), notify parent that thread is now persisted
              const userMessage = event.data?.userMessage;
              if (!threadId && userMessage?.threadId && onThreadChange) {
                onThreadChange(userMessage.threadId, true);
              }
              setIsSending(false);
              break;
            }

            case 'error':
              // Clear pending message on non-timeout errors
              if (event.data.error !== 'timeout') {
                pendingMessageRef.current = null;
              }
              console.error('Streaming error:', event.data.error);
              setIsSending(false);
              break;

            case 'ping':
            case 'placeholder_update':
            case 'message_update':
            case 'message_reference':
              // All these events are handled in the query cache
              break;
          }
        },
        {
          onTimeout: (originalMessage: string) => {
            setMessageInput(originalMessage);
            setIsSending(false);
            pendingMessageRef.current = null;
            setShowTimeoutError(true);
          },
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      pendingMessageRef.current = null;
      setIsSending(false);
    }
  };

  const handleSuggestionClick = (question: string) => {
    handleSendMessage(question);
  };

  return (
    <div className={`chatbot__container ${className}`}>
      <div className="chatbot__messages-container">
        <ChatMessages
          threadId={threadId}
          isThreadPersisted={isThreadPersisted}
          programId={programId}
          suggestions={suggestions}
          onSuggestionClick={handleSuggestionClick}
        />
      </div>

      <div className="chatbot__input-wrapper">
        <ChatInput
          messageInput={messageInput}
          onMessageChange={setMessageInput}
          onSendMessage={handleSendMessage}
          disabled={isSending}
        />
      </div>
      {showTimeoutError && (
        <Toast
          title={t('chatbot.errors.timeout')}
          variant="error"
          defaultOpen
        />
      )}
    </div>
  );
};

export default Chatbot;
